import { type ReactNode } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

interface MarkdownSurfaceProps {
  markdown: string
  onExternalLinkAttempt: () => void
}

function isExternalUrl(value: string) {
  return /^(https?:|mailto:)/i.test(value)
}

function renderLink(
  href: string | undefined,
  onExternalLinkAttempt: () => void,
  children: ReactNode,
) {
  if (!href) {
    return <span>{children}</span>
  }

  if (href.startsWith('#')) {
    return (
      <a href={href} className="reader-link">
        {children}
      </a>
    )
  }

  if (isExternalUrl(href)) {
    return (
      <a
        href={href}
        className="reader-link is-blocked"
        onClick={(event) => {
          event.preventDefault()
          onExternalLinkAttempt()
        }}
      >
        {children}
      </a>
    )
  }

  return <span className="reader-link is-blocked">{children}</span>
}

export function MarkdownSurface({ markdown, onExternalLinkAttempt }: MarkdownSurfaceProps) {
  return (
    <article className="reader-card">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a({ href, children }) {
            return renderLink(href, onExternalLinkAttempt, children)
          },
          blockquote({ children }) {
            return <blockquote className="reader-blockquote">{children}</blockquote>
          },
          code({ children, className, ...properties }) {
            const inline = !className
            return inline ? (
              <code className="reader-inline-code" {...properties}>
                {children}
              </code>
            ) : (
              <pre className="reader-code-block">
                <code {...properties}>{children}</code>
              </pre>
            )
          },
          img({ alt, src }) {
            if (src?.startsWith('data:') || src?.startsWith('blob:')) {
              return <img src={src} alt={alt ?? ''} className="reader-image" />
            }

            return (
              <figure className="image-placeholder">
                <div className="image-placeholder-box">Image omitted</div>
                <figcaption>{alt ?? 'External and relative images are disabled offline.'}</figcaption>
              </figure>
            )
          },
          table({ children }) {
            return (
              <div className="table-wrap">
                <table>{children}</table>
              </div>
            )
          },
        }}
      >
        {markdown}
      </ReactMarkdown>
    </article>
  )
}
